import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { FiCheckCircle } from 'react-icons/fi';

const ICON_SIZE = 36;

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.ghostWhite,
  dark: (props) => props.theme.colors.lpGhost,
});

const successColor = theme('mode', {
  light: (props) => props.theme.colors.softGrey,
  dark: (props) => props.theme.colors.blueGrey,
});

const backgroundColorVariant = theme.variants('mode', 'type', {
  active: {
    light: (props) => props.theme.colors.greyBlue,
    dark: (props) => props.theme.colors.richBlue,
  },
  inactive: {
    light: (props) => props.theme.colors.softGrey,
    dark: (props) => props.theme.colors.blueGrey,
  },
});

const lineColourVariant = theme.variants('mode', 'type', {
  cap: {
    light: 'transparent',
    dark: 'transparent',
  },
  normal: {
    light: (props) => props.theme.colors.superLightGrey,
    dark: (props) => props.theme.colors.richBlue,
  },
});

const labelColourVariant = theme.variants('mode', 'type', {
  active: {
    light: (props) => props.theme.colors.deepGrey,
    dark: (props) => props.theme.colors.midLightGrey,
  },
  inactive: {
    light: (props) => props.theme.colors.deepGrey,
    dark: (props) => props.theme.colors.midGrey,
  },
});

const Container = styled(Box)`
  background-color: ${backgroundColor};
  border-radius: ${(props) => props.theme.radius.small};
  overflow: auto;
  flex-shrink: 0;
  position: relative;
`;

const Wrapper = styled(Flex)`
  line-height: 1.4;
  font-weight: 300;
`;

const StepCircle = styled(Flex)`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: ${ICON_SIZE / 2}px;
  font-weight: 400;
  background-color: ${backgroundColorVariant};
  color: white;
`;

const Label = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  color: ${labelColourVariant};
`;

const Line = styled(Box)`
  height: 1px;
  background-color: ${lineColourVariant};
`;

const IconContainer = styled(Box)`
  overflow: visible;
  height: ${ICON_SIZE}px;
  width: ${ICON_SIZE}px;
  color: ${successColor};
`;

const CheckIcon = styled(FiCheckCircle)`
  stroke-width: 1.5px;
`;

function Stepper(props) {
  const { steps, activeStep, nocheck } = props;

  return (
    <Container py={4} borderRadius={3} mb={3}>
      <Wrapper fontSize={1} {...props}>
        {steps.map((step, index) => (
          <Box key={step.id} flex={1}>
            <Flex alignItems="center">
              <Line flex={1} type={index === 0 ? 'cap' : 'normal'} />
              {!nocheck && index === steps.length - 1 ? (
                <IconContainer>
                  <CheckIcon size={ICON_SIZE} />
                </IconContainer>
              ) : (
                <StepCircle
                  type={index === activeStep - 1 ? 'active' : 'inactive'}
                  alignItems="center"
                  justifyContent="center"
                  fontSize={1}
                >
                  {index + 1}
                </StepCircle>
              )}
              <Line flex={1} type={index + 1 >= steps.length ? 'cap' : 'normal'} />
            </Flex>
            <Label
              type={index === activeStep - 1 ? 'active' : 'inactive'}
              fontSize={0}
              fontWeight={index === activeStep - 1 ? 500 : 400}
              mt={[1, 2, 3]}
              mx={[1, 2, 3]}
            >
              {step.title}
            </Label>
          </Box>
        ))}
      </Wrapper>
    </Container>
  );
}

Stepper.defaultProps = {
  nocheck: false,
};

Stepper.propTypes = {
  activeStep: PropTypes.number.isRequired,
  steps: PropTypes.array.isRequired,
  nocheck: PropTypes.bool,
};

export default Stepper;
